import { Breadcrumb } from 'components/breadcrumbs';

/**
 * Represents a FAQ question-answer pair in Schema.org format
 * @see https://schema.org/Question
 */
export type SchemaQuestion = {
  /** Schema.org type identifier */
  '@type': 'Question';
  /** The question text */
  name: string;
  /** The answer to the question */
  acceptedAnswer: {
    /** Schema.org type identifier for the answer */
    '@type': 'Answer';
    /** The answer text */
    text: string;
  };
};

/**
 * Represents a single item in a Schema.org BreadcrumbList
 * @see https://schema.org/ListItem
 */
export type BreadcrumbListItem = {
  /** Schema.org type identifier */
  '@type': 'ListItem';
  /** Position of this item in the breadcrumb trail (1-based) */
  position: number;
  /** The breadcrumb item details */
  item: {
    /** URL of the page this breadcrumb links to */
    '@id': string;
    /** Display text for this breadcrumb */
    name: string;
  };
};

/**
 * Represents a Schema.org BreadcrumbList structure
 * @see https://schema.org/BreadcrumbList
 */
export type BreadcrumbList = {
  /** Schema.org context */
  '@context': 'https://schema.org';
  /** Schema.org type identifier */
  '@type': 'BreadcrumbList';
  /** Array of breadcrumb items */
  itemListElement: BreadcrumbListItem[];
};

/**
 * Creates a Schema.org ListItem for use in BreadcrumbList
 * @param name Display text for the breadcrumb
 * @param link URL that the breadcrumb points to
 * @param position Position in the breadcrumb trail (1-based)
 *
 * @returns A properly formatted Schema.org ListItem
 */
export const createBreadcrumbListItem = (
  name: string,
  link: string,
  position: number
): BreadcrumbListItem => ({
  '@type': 'ListItem',
  position,
  item: { '@id': link, name },
});

/**
 * Creates a Schema.org BreadcrumbList from an array of breadcrumbs
 * @param breadcrumbs Array of breadcrumb items containing name and link
 *
 * @returns A properly formatted Schema.org BreadcrumbList object
 */
export const createBreadcrumbList = (breadcrumbs: readonly Breadcrumb[]): BreadcrumbList => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map(({ name, link }, index) =>
    createBreadcrumbListItem(name, link, index + 1)
  ),
});

/**
 * Represents a Schema.org AggregateOffer
 * @see https://schema.org/AggregateOffer
 */
export type AggregateOffer = {
  /** Schema.org type identifier */
  '@type': 'AggregateOffer';
  /** Highest price among all offers */
  highPrice: number;
  /** Lowest price among all offers */
  lowPrice: number;
  /** Currency of the prices */
  priceCurrency: string;
};

/**
 * Creates a Schema.org AggregateOffer
 * @param highestPrice Highest price in the results
 * @param lowestPrice Lowest price in the results
 * @param currency Currency code for the prices
 *
 * @returns A properly formatted Schema.org AggregateOffer object
 */
export const createAggregateOffer = (
  highestPrice: number | null,
  lowestPrice: number | null,
  currency: string
): AggregateOffer => ({
  '@type': 'AggregateOffer',
  highPrice: highestPrice || 0,
  lowPrice: lowestPrice || 0,
  priceCurrency: currency,
});

/**
 * Represents a Schema.org Product
 * @see https://schema.org/Product
 */
export type ProductSchema = {
  /** Schema.org context */
  '@context': 'https://schema.org';
  /** Schema.org type identifier */
  '@type': 'Product';
  /** Name of the product */
  name: string;
  /** Offer information */
  offers: AggregateOffer;
  /** Language of the content */
  inLanguage?: string;
};

/**
 * Creates a Schema.org Product schema for real estate listings
 * @param name Full product name
 * @param currency Currency code for the prices
 * @param highestPrice Highest price in the results
 * @param lowestPrice Lowest price in the results
 *
 * @returns A properly formatted Schema.org Product object
 */
export const createProductSchema = (
  name: string,
  currency: string,
  highestPrice: number | null,
  lowestPrice: number | null
): ProductSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  offers: createAggregateOffer(highestPrice, lowestPrice, currency),
});

/**
 * Represents a Schema.org FAQPage
 * @see https://schema.org/FAQPage
 */
export type FAQPageSchema = {
  /** Schema.org context */
  '@context': 'https://schema.org';
  /** Schema.org type identifier */
  '@type': 'FAQPage';
  /** List of questions and answers */
  mainEntity: SchemaQuestion[];
  /** Language of the content */
  inLanguage?: string;
};

/**
 * Creates a Schema.org FAQPage schema
 * @param questions Array of questions and answers
 *
 * @returns A properly formatted Schema.org FAQPage object
 */
export const createFAQPageSchema = (questions: SchemaQuestion[]): FAQPageSchema => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions,
});